import { getAssignableStoresOptions, getStorePreviewToken, type TokenResponse } from '@gozoki/api'
import { FLAGS } from '@gozoki/feature-flags'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { SearchableSelect } from '~/components/new/app-ui/searchable-select'
import { useFeatureFlag, useFeatureFlagsReady } from '~/hooks/useFeatureFlag'

const STORE_APP_URL = import.meta.env.VITE_STORE_APP_URL as string | undefined

export const Route = createFileRoute('/_authenticated/store-app-preview')({
  component: StoreAppPreviewRoute,
})

// Écran gated par le flag `bo-navbar-store-app-preview` : prévisualise le store-app d'une boutique
// (choisie ici) directement dans le back-office via une iframe.
function StoreAppPreviewRoute() {
  const enabled = useFeatureFlag(FLAGS.boNavbarStoreAppPreview)
  const flagsReady = useFeatureFlagsReady()

  if (!flagsReady) {
    return <p className="p-4 text-sm text-muted-foreground">Chargement…</p>
  }

  if (!enabled) {
    return <Navigate to="/" replace />
  }

  return <StoreAppPreviewPage />
}

function StoreAppPreviewPage() {
  const { data: stores } = useQuery(getAssignableStoresOptions())
  const [storeId, setStoreId] = useState<number | null>(null)

  // Un token boutique est émis côté BO (contourne la whitelist d'IP) puis injecté dans l'iframe
  // via `?token=` (le store-app est sur une autre origine, son localStorage est inaccessible d'ici).
  const tokenMut = useMutation<TokenResponse, Error, number>({
    mutationFn: (id) => getStorePreviewToken(id),
  })

  const options = useMemo(
    () => (stores ?? []).map((store) => ({ value: String(store.id), label: store.label })),
    [stores],
  )

  const onSelect = (value: string) => {
    const id = Number(value)
    setStoreId(id)
    tokenMut.mutate(id)
  }

  const token = tokenMut.data?.token
  const iframeSrc =
    STORE_APP_URL && token ? `${STORE_APP_URL}/?token=${encodeURIComponent(token)}` : null

  const renderPreview = () => {
    if (!STORE_APP_URL) {
      return (
        <p className="text-sm text-destructive">
          VITE_STORE_APP_URL non défini : impossible d'afficher l'aperçu.
        </p>
      )
    }

    if (!iframeSrc) {
      return (
        <p className="text-sm text-muted-foreground">
          Sélectionnez une boutique pour afficher son store-app.
        </p>
      )
    }

    return (
      <>
        {/* Combinaison volontaire allow-scripts + allow-same-origin : le store-app est sur une
            AUTRE origine, donc allow-same-origin ne donne accès qu'à SA propre origine (requis
            pour lire son token en localStorage), jamais à celle du BO. */}
        {/* eslint-disable react/iframe-missing-sandbox */}
        <iframe
          key={iframeSrc}
          src={iframeSrc}
          title="Aperçu store-app"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
          className="min-h-0 w-full flex-1 rounded-lg border bg-white"
        />
        {/* eslint-enable react/iframe-missing-sandbox */}
      </>
    )
  }

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex items-center gap-4">
        <h1 className="text-lg font-semibold">Aperçu store-app</h1>
        <div className="w-80">
          <SearchableSelect
            value={storeId != null ? String(storeId) : ''}
            options={options}
            disabled={options.length === 0}
            triggerPlaceholder="Choisir une boutique…"
            searchPlaceholder="Rechercher une boutique"
            emptyMessage="Aucune boutique."
            onChange={onSelect}
          />
        </div>
        {tokenMut.isPending ? (
          <span className="text-sm text-muted-foreground">Chargement…</span>
        ) : null}
      </div>

      {renderPreview()}
    </div>
  )
}
